/* Phase 6 · CSV bulk upload — kind selector, sample preview, upload */
import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, Check, AlertTriangle, Link as LinkIcon, Plus } from "lucide-react";
import { Link } from "react-router-dom";

export default function CSVUpload() {
  const [kinds, setKinds] = useState([]);
  const [selected, setSelected] = useState("staff");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/csv-kinds").then(r => setKinds(r.data));
  }, []);

  const spec = kinds.find(k => k.kind === selected);

  const downloadSample = async () => {
    const r = await fetch(`${API}/admin/csv-sample/${selected}`, { credentials: "include" });
    const blob = await r.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `${selected}_sample.csv`;
    a.click(); URL.revokeObjectURL(u);
  };

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV file first");
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("kind", selected);
      fd.append("file", file);
      const r = await fetch(`${API}/admin/csv-upload`, {
        method: "POST", body: fd, credentials: "include",
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Upload failed");
      setResult(j);
      toast.success(`${j.created_or_updated} rows imported`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl" data-testid="csv-upload">
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">ADMIN · IMPORT</div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#0B2340]">CSV Bulk Upload</h1>
        <p className="text-xs text-[#7C8E9E] mt-1">
          Download the sample, fill it in, then upload. Existing rows are updated by natural key; new rows are added.
        </p>
      </div>

      <div className="bg-white p-4 rounded-lg border border-slate-100 mb-4">
        <label className="text-sm block mb-2">
          <span className="text-[#4A627A] text-xs">Data kind</span>
          <select className="mt-1 w-full border rounded px-2 py-2 text-sm"
            data-testid="csv-kind-select"
            value={selected} onChange={e => { setSelected(e.target.value); setFile(null); setResult(null); }}>
            {kinds.map(k => (
              <option key={k.kind} value={k.kind}>
                {k.label} · owner: {k.owner_role}{k.you_own ? " · YOU OWN" : ""}
              </option>
            ))}
          </select>
        </label>
      </div>

      {spec && (
        <div className="bg-white p-4 rounded-lg border border-slate-100 mb-4" data-testid="csv-spec">
          <div className="flex items-start gap-3 mb-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-[#0B2340]">{spec.label}</div>
              <div className="text-[11px] text-[#7C8E9E] mt-1">
                Owner: <b>{spec.owner_role}</b> · Collection: <code>{spec.collection}</code>
              </div>
              <div className="text-[11px] text-[#4A627A] mt-2">
                Columns:{" "}
                {spec.columns.map(c => (
                  <span key={c} className={`inline-block mr-1 mb-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${spec.required.includes(c) ? "bg-red-50 text-red-800" : "bg-slate-100 text-slate-600"}`}>
                    {c}{spec.required.includes(c) ? " *" : ""}
                  </span>
                ))}
              </div>
              <div className="text-[10px] text-[#B0B7BF] mt-1">* = required</div>
            </div>
            <button onClick={downloadSample} data-testid="csv-download-sample"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-semibold"
              style={{ background: "#1B3A5C" }}>
              <Download size={12} /> Download sample.csv
            </button>
          </div>

          <div className="border-t border-slate-100 pt-3 mt-2">
            <label className="text-sm">
              <span className="text-[#4A627A] text-xs">Upload filled CSV</span>
              <input type="file" accept=".csv,text/csv"
                data-testid="csv-file"
                className="mt-1 w-full text-xs"
                onChange={e => setFile(e.target.files?.[0] || null)} />
            </label>
            <button onClick={submit} disabled={!file || uploading} data-testid="csv-submit"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#B08B49] text-white text-sm font-semibold disabled:opacity-40">
              <Upload size={13} /> {uploading ? "Uploading…" : "Upload & import"}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-white p-4 rounded-lg border border-slate-100" data-testid="csv-result">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
              <Check size={16} />
            </div>
            <div>
              <div className="font-semibold">Import complete</div>
              <div className="text-xs text-[#7C8E9E]">
                {result.created_or_updated} rows written · {result.skipped} skipped · {result.errors.length} errors
              </div>
            </div>
          </div>
          {result.errors.length > 0 && (
            <div className="mt-2 p-2 bg-red-50 rounded border border-red-200 text-xs">
              <div className="font-semibold text-red-800 flex items-center gap-1 mb-1">
                <AlertTriangle size={12} /> Errors
              </div>
              <ul className="space-y-0.5 font-mono text-[11px] text-red-800">
                {result.errors.map((e, i) => (
                  <li key={i}>Line {e.line}: {e.error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Purchase Orders go through the canonical lifecycle importer */}
      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
        data-testid="csv-pos-notice">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-800 flex-shrink-0">
          <LinkIcon size={16} />
        </div>
        <div className="flex-1 text-xs text-[#4A627A]">
          <div className="font-semibold text-amber-900 text-sm">Purchase Orders → use Data Import</div>
          <div className="mt-1">
            Purchase Orders are imported via <b>Admin → Import (Handwoven / Quality / LSD)</b>,
            which builds the full PO lifecycle (weavers, looms, carpet lines, ETAs).
          </div>
          <Link to="/director/import" data-testid="csv-goto-po-import"
            className="mt-2 inline-block font-semibold text-[#B08B49] underline">
            Open Data Import →
          </Link>
        </div>
      </div>

      {/* Weaver alias mini-manager */}
      <WeaverAliases />
    </div>
  );
}

function WeaverAliases() {
  const [rows, setRows] = useState([]);
  const [alias, setAlias] = useState("");
  const [canonical, setCanonical] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/weaver-aliases").then(r => setRows(r.data)).catch(() => {});
  useEffect(load, []);

  const add = async () => {
    if (!alias.trim() || !canonical.trim()) {
      return toast.error("Both fields required");
    }
    setBusy(true);
    try {
      await api.post("/admin/weaver-aliases", { alias, canonical_name: canonical });
      toast.success("Alias saved");
      setAlias(""); setCanonical("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <div className="mt-4 bg-white p-4 rounded-lg border border-slate-100" data-testid="weaver-aliases">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">Weaver name aliases</div>
          <div className="text-xs text-[#4A627A]">
            CSV imports canonicalise weaver names via this list so misspellings resolve to one record.
          </div>
        </div>
      </div>
      <div className="flex gap-2 mb-3 flex-wrap">
        <input placeholder="Alias (as written in CSV)"
          data-testid="alias-input"
          value={alias} onChange={e => setAlias(e.target.value)}
          className="flex-1 min-w-[160px] border rounded px-2 py-1.5 text-sm" />
        <input placeholder="Canonical name"
          data-testid="canonical-input"
          value={canonical} onChange={e => setCanonical(e.target.value)}
          className="flex-1 min-w-[160px] border rounded px-2 py-1.5 text-sm" />
        <button onClick={add} disabled={busy} data-testid="alias-add-btn"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#B08B49] text-white text-xs font-semibold">
          <Plus size={12} /> {busy ? "Saving…" : "Add"}
        </button>
      </div>
      <div className="max-h-56 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">
            <tr><th className="text-left py-1.5">Alias</th><th className="text-left py-1.5">→ Canonical</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.alias_lower} data-testid={`alias-row-${r.alias_lower}`}
                className="border-t border-slate-100">
                <td className="py-1.5 font-mono">{r.alias_lower}</td>
                <td className="py-1.5">{r.canonical_name}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={2} className="py-3 text-center text-[#7C8E9E]">No aliases yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
